import { CommonModule, NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '@core/auth/auth.service';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { IEmpresa } from '@shared/models/Select/select';
import { AppAlertService } from '@shared/service/app/alert.service';
import { SelectsService } from '@shared/service/app/select.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector       : 'empresa-selector',
    templateUrl    : './empresa-selector.component.html',
    styleUrls      : ['./empresa-selector.component.scss'],
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone     : true,
    imports        : [CommonModule, MatButtonModule, MatDividerModule, MatIconModule, MatMenuModule, MatTooltipModule, NgClass, NgIf],
    providers      : [SelectsService]
})
export class EmpresaSelectorComponent implements OnInit, OnDestroy
{
    empresas: IEmpresa[] = [];
    empresaActualId: number;
    user: User;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _userService: UserService,
        private _selectService: SelectsService,
        private confirm: FuseConfirmationService,
        private alert: AppAlertService,
        private _authService: AuthService
    ) {}

    get empresaActualNombre(): string {
        const emp = this.empresas.find(e => e.id === this.empresaActualId);
        return emp ? emp.descripcion : 'Seleccionar Empresa';
    }

    async ngOnInit() {
        // Suscribirse a cambios del usuario
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) =>
            {
                if (user) {
                    this.user = user;
                    this.empresaActualId = user.idempresa;
                    this._changeDetectorRef.markForCheck();
                }
            });

        // Obtener la lista de empresas
        try {
            this.empresas = await this._selectService.getSelectByModel<IEmpresa>(
                'Empresa',
                'idempresa',
                '{razon_social}'
            );
            this._changeDetectorRef.markForCheck();
        } catch (error) {
            console.error('Error al cargar empresas:', error);
        }
    }

    seleccionarEmpresa(empresa: IEmpresa): void {
        this.confirm.open({
            title: "Confirmar cambio de empresa",
            message: "¿Desea cambiar a " + empresa.descripcion + "? Se recargará la página para aplicar la empresa seleccionada.",
            dismissible: false,
            icon: {
                show: false
            },
            actions: {
                confirm: {
                    show: true,
                    color: 'primary',
                    label: 'Confirmar',
                },
                cancel: {
                    show: true,
                    label: 'Cancelar'
                }
            },
        })
            .afterClosed()
            .subscribe(res => {
                if (res === 'confirmed') {
                    const payload = {
                        idempresa: empresa.id
                    };
                    this._userService.cambiarEmpresa(payload).subscribe({
                        next: (data) => {
                            // Actualiza el token con el cambio de empresa
                            this._authService.accessToken = data.token;
                            // Actualiza el idempresa del user seteado en el localstorage
                            const user = JSON.parse(localStorage.getItem('user') ?? '{}');
                            user.idempresa = data.result;

                            this._userService.user = user;
                            this._userService['_user'].next(user);

                            // Cambio para poder ver la empresa seteada que se cambio
                            this.empresaActualId = data.result;
                            
                            // Bandera para mostrar mensaje luego del reload
                            localStorage.setItem('empresaCambiada', 'true');

                            // Si estamos dentro del módulo de cuotas de alquileres, redirigir a la pantalla principal de cuotas
                            if (window.location.hash.includes('/cuotas')) {
                                window.location.hash = '#/cuotas';
                            }

                            // Recargar la app
                            location.reload();
                        },
                        error: (error) => {
                            console.error(error);
                        }
                    });
                }
            });
    }

    ngOnDestroy(): void
    {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
